/**
 * TodoSortableItem - Animated wrapper for items inside a sortable list.
 * Handles position animation (translate + scale + shadow) based on shared drag
 * state, and reports its measured height so the reorder math stays accurate for
 * variable-height rows (cards with subtasks, multi-line titles).
 */

import React, { useCallback, type ReactNode } from 'react';
import { type LayoutChangeEvent } from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  withTiming,
  type SharedValue,
} from 'react-native-reanimated';

import { sortableYOffset } from '@/lib/todo/sortable-geometry';

type TodoSortableItemProps = {
  index: number;
  activeIndex: SharedValue<number>;
  hoverIndex: SharedValue<number>;
  dragY: SharedValue<number>;
  heights: SharedValue<number[]>;
  taskId: string;
  onMeasure: (taskId: string, height: number) => void;
  children: ReactNode;
};

export function TodoSortableItem({
  index,
  activeIndex,
  hoverIndex,
  dragY,
  heights,
  taskId,
  onMeasure,
  children,
}: TodoSortableItemProps) {
  const handleLayout = useCallback(
    (event: LayoutChangeEvent) => {
      onMeasure(taskId, event.nativeEvent.layout.height);
    },
    [onMeasure, taskId],
  );

  const animatedStyle = useAnimatedStyle(() => {
    const { yOffset, isActive } = sortableYOffset(
      index,
      activeIndex.value,
      hoverIndex.value,
      dragY.value,
      heights.value,
    );

    return {
      transform: [
        {
          translateY: isActive
            ? yOffset
            : withTiming(yOffset, { duration: 150, easing: Easing.out(Easing.quad) }),
        },
        { scale: withTiming(isActive ? 1.03 : 1, { duration: 120 }) },
      ],
      opacity:
        activeIndex.value === -1
          ? withTiming(1, { duration: 150 })
          : isActive
            ? 1
            : withTiming(0.5, { duration: 150 }),
      zIndex: isActive ? 100 : 0,
      shadowOpacity: isActive ? 0.15 : 0,
    };
  });

  return (
    <Animated.View style={animatedStyle} onLayout={handleLayout}>
      {children}
    </Animated.View>
  );
}
